import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import debounce from "lodash.debounce";
import { useMessageBus } from "@jti/fe-message-bus";
import { UserMarkerInfoWindow } from "../../../../components/StoreLocator/components/UserMarkerInfoWindow";
import { DARK_MAP_STYLES_CONFIG, LIGHT_MAP_STYLES_CONFIG } from "../../../../components/StoreLocator/store-locator.config";
import { createStoreIconFactory, USER_LOCATION } from "../../../../components/StoreLocator/store-locator-icons";
import createCustomInfoOverlay from "../../../../components/StoreLocator/utils/CustomGoogleInfoOverlay";
import { useConfig } from "../../../../providers/useConfig";
import { createClusterer } from "./uitls";
const ENTER_KEY = 13;
const DOWN_ARROW_KEY = 40;
export const GOOGLE_DIRECTION_URL_CONST = "https://www.google.com/maps/dir/?api=1&destination=";
export const useGoogleMapsStoreLocator = ({ defaultLat, defaultLng, defaultZoom, activeStoreId, handleSelectStore, handleLoadFlotiqStores, defaultStoreIcon, storeIcons, isSearchVisible, onActivity, onTap, }) => {
    const { ISO_COUNTRY_CODE, storeLocatorMapTheme = "dark", storeLocatorLayoutVersion: layoutVersion } = useConfig();
    const mapRef = useRef(null);
    const inputRef = useRef(null);
    const googleMapRef = useRef(null);
    const infoWindowRef = useRef(null);
    const autocompleteRef = useRef(null);
    const infoOverlayRef = useRef(null);
    const markersRef = useRef({});
    const isMarkerClickRef = useRef(false);
    const activeMarkerRef = useRef(null);
    const tapTimeout = useRef(null);
    const [markerCluster, setMarkerCluster] = useState(null);
    const [isMapLoading, setIsMapLoading] = useState(false);
    const [isMapError, setIsMapError] = useState(false);
    const [clusterClickTimestamp, setClusterClickTimestamp] = useState(0);
    const [isNoStoresSearch, setIsNoStoresSearch] = useState(null);
    const messageBus = useMessageBus();
    const isDarkTheme = storeLocatorMapTheme === "dark";
    const mapStyles = isDarkTheme ? DARK_MAP_STYLES_CONFIG : LIGHT_MAP_STYLES_CONFIG;
    const storeIconMap = useMemo(() => (storeIcons ?? []).reduce((map, { storeType, storeIcon }) => {
        if (storeType && storeIcon) {
            map[storeType] = storeIcon;
        }
        return map;
    }, {}), [storeIcons]);
    const getStoreIcon = useCallback(createStoreIconFactory({
        layoutVersion,
        theme: storeLocatorMapTheme,
        defaultStoreIcon,
        storeTypeOverrides: storeIconMap,
    }), [defaultStoreIcon, storeIconMap, layoutVersion, storeLocatorMapTheme]);
    const clustererFactory = createClusterer({ layoutVersion, theme: storeLocatorMapTheme });
    const throwConfigError = () => {
        setIsMapError(true);
        return "";
    };
    const waitForMapIdle = (callback) => {
        if (!googleMapRef.current)
            return;
        google.maps.event.addListenerOnce(googleMapRef.current, "idle", callback);
    };
    const options = useMemo(() => ({
        componentRestrictions: {
            country: ISO_COUNTRY_CODE || throwConfigError(),
        },
        fields: ["geometry", "name"],
        types: ["geocode", "establishment"],
    }), [ISO_COUNTRY_CODE]);
    const debouncedLoadStores = useCallback(debounce((map) => {
        if (!isMarkerClickRef.current) {
            handleLoadFlotiqStores(map);
        }
    }, 300), [handleLoadFlotiqStores]);
    const handleLoadMarkers = (stores) => {
        if (!googleMapRef.current) {
            return;
        }
        if (stores.length > 0) {
            setIsNoStoresSearch(null);
        }
        const currentActiveMarkerId = activeMarkerRef.current?.get("storeData")?.id;
        const newMarkersMap = {};
        if (markerCluster) {
            markerCluster.clearMarkers();
        }
        requestAnimationFrame(() => {
            stores.forEach((store) => {
                const { Location, id } = store;
                if (markersRef.current[id]) {
                    const existingMarker = markersRef.current[id];
                    existingMarker.set("storeData", store);
                    const position = existingMarker.getPosition();
                    if (position && (position.lat() !== Location.lat || position.lng() !== Location.lon)) {
                        existingMarker.setPosition({
                            lat: Location.lat,
                            lng: Location.lon,
                        });
                    }
                    newMarkersMap[id] = existingMarker;
                    return existingMarker;
                }
                const markerIcon = getStoreIcon(store);
                const icon = { url: markerIcon.default };
                const marker = new google.maps.Marker({
                    optimized: true,
                    title: id,
                    position: {
                        lng: Location.lon,
                        lat: Location.lat,
                    },
                    icon,
                });
                marker.set("defaultIcon", icon);
                marker.set("storeData", store);
                marker.addListener("click", () => {
                    handleMarkerEvent(marker, true);
                });
                marker.addListener("dblclick", () => {
                    handleMarkerEvent(marker, false);
                });
                newMarkersMap[id] = marker;
            });
            markersRef.current = newMarkersMap;
            if (googleMapRef.current) {
                const currentMarkerCluster = clustererFactory(googleMapRef.current, Object.values(newMarkersMap));
                if (typeof google.maps.event.addListener === "function" &&
                    currentMarkerCluster &&
                    typeof currentMarkerCluster.addListener === "function") {
                    currentMarkerCluster.addListener("click", () => {
                        setClusterClickTimestamp(Date.now());
                    });
                }
                setMarkerCluster(currentMarkerCluster);
            }
            if (currentActiveMarkerId && newMarkersMap[currentActiveMarkerId]) {
                const markerToActivate = newMarkersMap[currentActiveMarkerId];
                waitForMapIdle(() => {
                    handleMarkerEvent(markerToActivate, false);
                });
            }
        });
    };
    const handleMarkerEvent = (marker, shouldCenter) => {
        isMarkerClickRef.current = true;
        if (activeMarkerRef.current && activeMarkerRef.current !== marker) {
            activeMarkerRef.current.setIcon(activeMarkerRef.current.get("defaultIcon"));
        }
        const store = marker.get("storeData");
        const markerIcon = getStoreIcon(store);
        marker.setIcon({ url: markerIcon.active });
        activeMarkerRef.current = marker;
        if (shouldCenter && googleMapRef.current) {
            debouncedLoadStores.cancel();
            const position = marker.getPosition();
            if (position) {
                googleMapRef.current.setCenter({
                    lat: position.lat(),
                    lng: position.lng(),
                });
                const wasRecentClusterClick = Date.now() - clusterClickTimestamp < 1000;
                if (!wasRecentClusterClick) {
                    const currentZoom = googleMapRef.current.getZoom();
                    if (currentZoom < 15) {
                        const bounds = new google.maps.LatLngBounds();
                        const lat = position.lat();
                        const lng = position.lng();
                        const offset = 0.001;
                        bounds.extend(new google.maps.LatLng(lat - offset, lng - offset));
                        bounds.extend(new google.maps.LatLng(lat + offset, lng + offset));
                        googleMapRef.current.fitBounds(bounds);
                    }
                }
            }
            waitForMapIdle(() => {
                handleSelectStore(marker.getTitle() || "");
                if (googleMapRef.current) {
                    handleLoadFlotiqStores(googleMapRef.current);
                }
                isMarkerClickRef.current = false;
            });
        }
        else {
            handleSelectStore(marker.getTitle() || "");
            waitForMapIdle(() => {
                isMarkerClickRef.current = false;
            });
        }
    };
    const handleInitMap = useCallback(() => {
        setIsMapLoading(true);
        const pos = {
            lat: Number(defaultLat),
            lng: Number(defaultLng),
        };
        const map = new google.maps.Map(mapRef.current, {
            center: { lat: pos.lat, lng: pos.lng },
            zoom: defaultZoom,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            styles: mapStyles,
        });
        const infoWindow = new google.maps.InfoWindow({
            content: "",
            disableAutoPan: true,
        });
        infoWindowRef.current = infoWindow;
        googleMapRef.current = map;
        infoOverlayRef.current = createCustomInfoOverlay(map);
        infoOverlayRef.current.addCloseHandler(() => {
            if (activeMarkerRef.current) {
                activeMarkerRef.current.setIcon(activeMarkerRef.current.get("defaultIcon"));
                activeMarkerRef.current = null;
            }
            handleSelectStore("");
        });
        google.maps.event.addListenerOnce(map, "bounds_changed", () => {
            handleLoadFlotiqStores(googleMapRef.current);
        });
        infoWindow.addListener("closeclick", () => {
            if (activeMarkerRef.current) {
                activeMarkerRef.current.setIcon(activeMarkerRef.current.get("defaultIcon"));
                activeMarkerRef.current = null;
            }
            handleSelectStore("");
        });
        const handleTap = () => {
            if (tapTimeout.current) {
                clearTimeout(tapTimeout.current);
            }
            tapTimeout.current = window.setTimeout(() => {
                onTap();
                tapTimeout.current = null;
            }, 180);
        };
        const cancelTap = () => {
            if (tapTimeout.current) {
                clearTimeout(tapTimeout.current);
                tapTimeout.current = null;
            }
            onActivity();
        };
        const tapListener = map.addListener("click", handleTap);
        const gestureEvents = ["dragstart", "zoom_changed", "wheel", "touchmove"];
        const gestureListeners = gestureEvents.map((evt) => map.addListener(evt, cancelTap));
        const idleListener = map.addListener("idle", () => {
            debouncedLoadStores(googleMapRef.current);
        });
        setIsMapLoading(false);
        return () => {
            idleListener.remove();
            tapListener.remove();
            gestureListeners.forEach((l) => l.remove());
            if (tapTimeout.current)
                clearTimeout(tapTimeout.current);
        };
    }, [defaultLat, defaultLng, defaultZoom, handleSelectStore, debouncedLoadStores, storeLocatorMapTheme, mapStyles]);
    const handleLoadUserMarker = (userLat, userLng) => {
        if (!googleMapRef.current) {
            return;
        }
        const image = {
            url: USER_LOCATION,
            scaledSize: new google.maps.Size(30, 30),
        };
        const container = document.createElement("div");
        const root = createRoot(container);
        root.render(_jsx(UserMarkerInfoWindow, {}));
        const userLocationInfoWindow = new google.maps.InfoWindow({
            content: container,
            disableAutoPan: true,
        });
        const userLocationMarker = new google.maps.Marker({
            position: {
                lat: userLat,
                lng: userLng,
            },
            icon: image,
            map: googleMapRef.current,
        });
        userLocationMarker.addListener("click", () => {
            if (googleMapRef.current) {
                userLocationInfoWindow.open(googleMapRef.current, userLocationMarker);
            }
        });
        isMarkerClickRef.current = true;
        googleMapRef.current.setCenter({
            lat: userLat,
            lng: userLng,
        });
        waitForMapIdle(async () => {
            isMarkerClickRef.current = false;
            await handleLoadFlotiqStores(googleMapRef.current);
            setIsNoStoresSearch({ value: true, type: "location" });
        });
    };
    const handleSearchForm = useCallback((place) => {
        messageBus.publish("tracking.event", {
            payload: {
                event_name: "store_locator_search",
                additionalData: {
                    search_term: inputRef.current?.value,
                },
            },
        });
        setIsNoStoresSearch({ value: true, type: "search" });
        if (place?.geometry) {
            isMarkerClickRef.current = true;
            if (place.geometry.viewport) {
                googleMapRef?.current?.fitBounds(place.geometry.viewport);
            }
            else {
                googleMapRef?.current?.setCenter(place.geometry.location);
            }
            inputRef.current.blur();
            waitForMapIdle(() => {
                isMarkerClickRef.current = false;
                handleLoadFlotiqStores(googleMapRef.current);
            });
        }
    }, []);
    const handleInputSearchClick = () => {
        const noop = () => null;
        google.maps.event.trigger(inputRef.current, "focus", {});
        google.maps.event.trigger(inputRef.current, "keydown", {
            keyCode: DOWN_ARROW_KEY,
            stopPropagation: noop,
            preventDefault: noop,
        });
        google.maps.event.trigger(inputRef.current, "keydown", { keyCode: ENTER_KEY });
        google.maps.event.trigger(googleMapRef.current, "zoom_changed", {});
    };
    const onPlaceChanged = useCallback(() => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry) {
            return;
        }
        handleSearchForm(place);
    }, []);
    const handleAddCustomEventListenerWrapper = () => {
        if (inputRef.current) {
            const _addEventListener = inputRef.current.addEventListener;
            const addEventListenerWrapper = (type, listener) => {
                if (type === "keydown") {
                    const originalListener = listener;
                    listener = function (event) {
                        const suggestionSelected = document.querySelector(".pac-item-selected");
                        if (event.which === ENTER_KEY && !suggestionSelected) {
                            const simulatedEvent = new KeyboardEvent("keydown", {
                                keyCode: DOWN_ARROW_KEY,
                                which: DOWN_ARROW_KEY,
                            });
                            originalListener.apply(inputRef.current, [simulatedEvent]);
                        }
                        originalListener.apply(inputRef.current, [event]);
                    };
                }
                _addEventListener.apply(inputRef.current, [type, listener]);
            };
            inputRef.current.addEventListener = addEventListenerWrapper;
        }
    };
    const handleMountAutoComplete = useCallback(() => {
        if (inputRef.current && googleMapRef.current && !autocompleteRef.current) {
            autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
            autocompleteRef.current.bindTo("bounds", googleMapRef.current);
            autocompleteRef.current.addListener("place_changed", onPlaceChanged);
            handleAddCustomEventListenerWrapper();
        }
    }, [onPlaceChanged, options]);
    const handleSelectStoreFromList = (storeId) => {
        if (!storeId || !googleMapRef.current)
            return;
        const activeMarker = markersRef.current[storeId];
        if (activeMarker) {
            debouncedLoadStores.cancel();
            handleMarkerEvent(activeMarker, true);
        }
        else {
            handleSelectStore(storeId);
        }
    };
    useEffect(() => {
        handleMountAutoComplete();
    });
    useEffect(() => {
        if (activeStoreId) {
            const activeMarker = markersRef.current[activeStoreId];
            if (activeMarker && google) {
                handleMarkerEvent(activeMarker, false);
            }
            else {
                handleSelectStore("");
                infoWindowRef.current?.close();
                infoOverlayRef.current?.close();
            }
        }
    }, [activeStoreId]);
    useEffect(() => {
        return () => {
            if (infoOverlayRef.current) {
                infoOverlayRef.current.setMap(null);
            }
            debouncedLoadStores.cancel();
        };
    }, [debouncedLoadStores]);
    const handleMapExpansion = () => {
        const currentZoom = googleMapRef.current.getZoom();
        const mapExpansionZoom = 7;
        const zoom = currentZoom > mapExpansionZoom ? mapExpansionZoom : currentZoom - 1;
        googleMapRef.current.setZoom(zoom);
    };
    return {
        mapRef,
        isSearchVisible,
        inputRef,
        isMapError,
        isMapLoading,
        handleLoadUserMarker,
        handleLoadMarkers,
        handleInitMap,
        handleInputSearchClick,
        handleSelectStoreFromList,
        isNoStoresSearch,
        handleMapExpansion,
    };
};
//# sourceMappingURL=useGoogleMapsStoreLocator.js.map